import {
  AiPromptUseCase,
  type ExternalChannelRailItemId,
} from '../../admin/types/AiPromptConfig';
import { isWebchatParticipant } from '../conversationChannelIcons';
import { Conversation, ConversationParticipantRef } from '../types/Conversation';
import { isExternalConversation, isWhatsAppGroupConversation } from './conversationClassification';
import { inferContactChannelTypeFromMessageSource } from './conversationListRealtime';
import { resolveConversationHasInstagramComments } from './instagramComposerUtils';
import { resolveConversationHasMessengerComments } from './messengerComposerUtils';

export type ConversationAiUseCaseTarget = {
  tab: typeof AiPromptUseCase.ReplyDefault | typeof AiPromptUseCase.ReplyExternalUnmuted;
  channel?: ExternalChannelRailItemId;
  labelKey: string;
  labelDefault: string;
};

const channelFromContactType = (
  contactChannelType: ConversationParticipantRef['contactChannelType'],
  participant?: Pick<ConversationParticipantRef, 'contactChannelType' | 'contactChannelLabel' | 'contactChannelValue'>,
): ExternalChannelRailItemId | undefined => {
  if (participant && isWebchatParticipant(participant)) {
    return 'webchat';
  }

  switch (contactChannelType) {
    case 'whatsApp':
      return 'whatsapp';
    case 'instagram':
      return 'instagram';
    case 'telegram':
      return 'telegram';
    case 'messenger':
      return 'messenger';
    case 'tikTok':
      return 'tiktok';
    default:
      return undefined;
  }
};

const channelFromMessageSource = (source?: string): ExternalChannelRailItemId | undefined => {
  if ((source || '').trim().toLowerCase() === 'webchat') {
    return 'webchat';
  }

  return channelFromContactType(inferContactChannelTypeFromMessageSource(source));
};

const resolveExternalCustomerChannel = (
  conversation: Pick<Conversation, 'containsWhatsAppMessages' | 'containsInstagramMessages' | 'participants' | 'messages'>,
): ExternalChannelRailItemId | undefined => {
  if (conversation.containsWhatsAppMessages) {
    return 'whatsapp';
  }

  if (conversation.containsInstagramMessages) {
    return 'instagram';
  }

  const externalParticipant = conversation.participants.find(
    (participant) => participant.type === 'customer' || participant.type === 'lead',
  );
  const participantChannel = externalParticipant
    ? channelFromContactType(externalParticipant.contactChannelType, externalParticipant)
    : undefined;
  if (participantChannel) {
    return participantChannel;
  }

  const latestMessage = conversation.messages.reduce<Conversation['messages'][number] | null>((latest, message) => {
    if (!latest) {
      return message;
    }

    return new Date(message.timestamp).getTime() >= new Date(latest.timestamp).getTime()
      ? message
      : latest;
  }, null);

  return channelFromMessageSource(latestMessage?.source);
};

const customerTarget = (
  channel: ExternalChannelRailItemId | undefined,
  labelKey: string,
  labelDefault: string,
): ConversationAiUseCaseTarget => ({
  tab: AiPromptUseCase.ReplyExternalUnmuted,
  channel,
  labelKey,
  labelDefault,
});

export const resolveConversationAiUseCaseTarget = (
  conversation: Pick<
    Conversation,
    | 'participants'
    | 'hasExternalParticipant'
    | 'conversationKind'
    | 'customListId'
    | 'whatsAppGroupJid'
    | 'containsWhatsAppMessages'
    | 'containsInstagramMessages'
    | 'containsInstagramComments'
    | 'containsMessengerComments'
    | 'messages'
  >,
): ConversationAiUseCaseTarget => {
  if (isWhatsAppGroupConversation(conversation)) {
    return customerTarget('whatsapp-groups', 'aiSettings.useCases.whatsAppGroup.tabLabel', 'WhatsApp groups');
  }

  if (resolveConversationHasInstagramComments(conversation)) {
    return customerTarget('instagram-comments', 'aiSettings.useCases.instagramComment.tabLabel', 'Instagram comments');
  }

  if (resolveConversationHasMessengerComments(conversation)) {
    return customerTarget('messenger-comments', 'aiSettings.useCases.messengerComment.tabLabel', 'Facebook comments');
  }

  if (isExternalConversation(conversation)) {
    const channel = resolveExternalCustomerChannel(conversation);
    switch (channel) {
      case 'whatsapp':
        return customerTarget(channel, 'aiSettings.externalChannels.whatsapp', 'WhatsApp');
      case 'telegram':
        return customerTarget(channel, 'aiSettings.externalChannels.telegram', 'Telegram');
      case 'instagram':
        return customerTarget(channel, 'aiSettings.externalChannels.instagram', 'Instagram');
      case 'messenger':
        return customerTarget(channel, 'aiSettings.externalChannels.messenger', 'Messenger');
      case 'webchat':
        return customerTarget(channel, 'aiSettings.externalChannels.webchat', 'Web chat');
      case 'tiktok':
        return customerTarget(channel, 'aiSettings.externalChannels.tiktok', 'TikTok');
      default:
        return customerTarget(undefined, 'aiSettings.useCases.externalUnmuted.tabLabel', 'Customer chats');
    }
  }

  return {
    tab: AiPromptUseCase.ReplyDefault,
    labelKey: 'aiSettings.useCases.reply.tabLabel',
    labelDefault: 'Team chats',
  };
};

export const buildConversationAiUseCasePath = (target: ConversationAiUseCaseTarget): string => {
  const params = new URLSearchParams();
  params.set('tab', target.tab);
  if (target.channel) {
    params.set('channel', target.channel);
  }

  return `/ai/config?${params.toString()}`;
};
